import { useRef } from "react";
import type { TextareaHTMLAttributes } from "react";
import { Label } from "./Label";
import { FieldError } from "./FieldError";
import { twMerge } from "tailwind-merge";

interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  optional?: boolean;
  errorMessage?: string;
}

export function TextareaField({
  label,
  optional,
  errorMessage,
  className,
  onInput,
  ...props
}: TextareaFieldProps) {
  const hasError = Boolean(errorMessage);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInput: NonNullable<
    TextareaHTMLAttributes<HTMLTextAreaElement>["onInput"]
  > = (e) => {
    const el = e.currentTarget;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    onInput?.(e);
  };

  return (
    <div className={twMerge("flex flex-col gap-2", className)}>
      {label && (
        <Label htmlFor={props.name} optional={optional}>
          {label}
        </Label>
      )}

      <textarea
        ref={textareaRef}
        id={props.name}
        onInput={handleInput}
        {...props}
        className={twMerge(
          "min-h-[140px] w-full overflow-hidden rounded-[8px] border border-[#E5E7EB] bg-[#F9FAFB] px-[14px] py-[10px] text-[14px] font-medium text-[#1A1F2E] transition-colors duration-150 outline-none",
          "placeholder:text-[#00000080]",
          "resize-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          hasError && "border-[#E02800] focus:border-[#E02800]",
        )}
      />

      {hasError && <FieldError message={errorMessage!} />}
    </div>
  );
}
